import { useAuth0 } from '@auth0/auth0-react';
import React, { Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import useApiRequest from '../../lib/hooks/useRequest';
import { UserInformationResponseDto, UserType } from '../../lib/interfaces/user';
import ErrorModal from '../../ui/components/ui/error-modal';
import Preloader from '../../ui/components/ui/preloader';
import {
  BannerNotificationResponseDto,
  BannerCancellationNotificationListResponseDto,
  BannerNotificationListResponseDto
} from '../../lib/interfaces/notifications';
import { Paginated } from '../../lib/interfaces/utils';

interface IContext {
  isMobile: boolean;
  isTablet: boolean;
  accessToken: string;
  isBanned: boolean;
  user?: UserInformationResponseDto; // UserPrivateResponseDto
  error?: Error;
  setIsBanned?: Dispatch<SetStateAction<boolean>>;
  setAccessToken?: Dispatch<SetStateAction<string>>;
  setUser?: Dispatch<SetStateAction<UserInformationResponseDto | undefined>>;
  setError?: Dispatch<SetStateAction<Error | undefined>>;
  canceledDate?: string | null;
  setCanceledDate?: Dispatch<SetStateAction<string | null>>;
  bannerNotifications?: BannerNotificationResponseDto[];
  setBannerNotifications?: Dispatch<SetStateAction<BannerNotificationResponseDto[]>>;
}

export enum ErrorEntity {
  NoOrganizationForbidden = 'NoOrganizationForbidden',
  IncompleteOnboardingUserInvitationsForbidden = 'IncompleteOnboardingUserInvitationsForbidden',
  IncompleteOnboardingIntroductionVideoForbidden = 'IncompleteOnboardingIntroductionVideoForbidden',
  EMailNotVerifiedForbidden = 'EMailNotVerifiedForbidden',
  Conflict = 'Conflict',
  NoUserFoundForbidden = 'NoUserFoundForbidden',
  PaymentOverdueForbidden = 'PaymentOverdueForbidden',
  NoUserFoundSelfCheckoutForbidden = 'NoUserFoundSelfCheckoutForbidden',
  UserAwaitsApprovalForbidden = 'UserAwaitsApprovalForbidden',
  InvalidIbanForbidden = 'InvalidIbanForbidden',
  InvalidBicForbidden = 'InvalidBicForbidden',
  InvalidIbanAndBicForbidden = 'InvalidIbanAndBicForbidden',
  TotalFileSizeExceededBadRequest = 'TotalFileSizeExceededBadRequest',
  InvalidCodeBadRequest = 'InvalidCodeBadRequest',
  MultipleAssignmentEnabledConflict = 'MultipleAssignmentEnabledConflict',
  MultipleAssignmentDisabledConflict = 'MultipleAssignmentDisabledConflict',
  MultipleAssignmentDisableNotUniqueConflict = 'MultipleAssignmentDisableNotUniqueConflict',
  AssignedCapacityMismatchBadRequest = 'AssignedCapacityMismatchBadRequest',
  DuplicateAssignedInternalUserBadRequest = 'DuplicateAssignedInternalUserBadRequest',
  FutureCapacityMultipleAssignmentConflict = 'FutureCapacityMultipleAssignmentConflict',
  JobFollowersMultipleAssignmentDisabledConflict = 'JobFollowersMultipleAssignmentDisabledConflict',
  JobFollowerNotAssignedConflict = 'JobFollowerNotAssignedConflict',
  OrganizationLockedByOfferConflict = 'OrganizationLockedByOfferConflict',
  OfferMailAlreadyInUseConflict = 'OfferMailAlreadyInUseConflict',
  OfferCompanyNameAlreadyInUseConflict = 'OfferCompanyNameAlreadyInUseConflict',
  OfferProductNotAvailableBadRequest = 'OfferProductNotAvailableBadRequest',
  OfferAddOnNotAvailableBadRequest = 'OfferAddOnNotAvailableBadRequest',
  OfferStartDateInvalidBadRequest = 'OfferStartDateInvalidBadRequest',
  OfferNotOpenConflict = 'OfferNotOpenConflict',
  OfferLinkInvalidNotFound = 'OfferLinkInvalidNotFound',
  OfferAlreadyAcceptedConflict = 'OfferAlreadyAcceptedConflict',
  OfferPaymentDetailsInvalidBadRequest = 'OfferPaymentDetailsInvalidBadRequest',
  OfferCheckoutTooManyRequests = 'OfferCheckoutTooManyRequests',
  OfferNotDraftConflict = 'OfferNotDraftConflict',
  OfferNotSentConflict = 'OfferNotSentConflict',
  OfferArchivedConflict = 'OfferArchivedConflict',
  OfferNotClosedConflict = 'OfferNotClosedConflict',
  OfferNotAcceptedConflict = 'OfferNotAcceptedConflict'
}

export interface Error {
  message: string;
  title: string;
  error: string;
  statusCode: number;
  entity: ErrorEntity;
  location: {
    path: string;
    redirect: boolean;
  };
  isClosable?: boolean;
  withReload?: boolean;
  mail?: string;
  name?: string;
  maxFileSize?: number;
}

export const Context = React.createContext<IContext>({
  isMobile: false,
  isTablet: false,
  accessToken: '',
  isBanned: false,
  canceledDate: null
});

export const ContextProvider = ({ children }: any) => {
  const setSessionToken = () => {
    const currentSessionToken = sessionStorage.getItem('session_token');
    if (!currentSessionToken) {
      sessionStorage.setItem('session_token', uuidv4());
    }
  };

  setSessionToken();

  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState<boolean>(window.innerWidth <= 1024);
  const [accessToken, setAccessToken] = useState<string>('');
  const [user, setUser] = useState<any | undefined>();
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const [canceledDate, setCanceledDate] = useState<string | null>(null);
  const [bannerNotifications, setBannerNotifications] = useState<BannerNotificationResponseDto[]>([]);
  const [accessExpired, setAccessExpired] = useState<boolean>(false);
  const auth0 = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 768);
    setIsTablet(window.innerWidth <= 1024);
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (setAccessToken) {
        const x = await auth0.getAccessTokenSilently({
          authorizationParams: { scope: 'openid profile email offline_access' }
        });
        setAccessToken(x);
      }
    };

    const synchronizeRoute = () => {
      const pathFromWindow = window.location.pathname;
      if (location.pathname !== pathFromWindow) {
        navigate(pathFromWindow, { replace: true }); // Synchronize route
      }
    };

    if (auth0.isAuthenticated) {
      fetchAccessToken().then(synchronizeRoute);
    } else if (!auth0.isLoading) {
      if (window.location.pathname === '/access-expired') {
        setAccessExpired(true);
        return;
      }
      // Check for access expired message from Auth0
      const searchParams = new URLSearchParams(window.location.search);
      const message = searchParams.get('message');
      const success = searchParams.get('success');

      if (success === 'false' && message) {
        const messageLower = message.toLowerCase();
        if (messageLower.includes('expired') || messageLower.includes('only once') || messageLower.includes('abgelaufen')) {
          // Navigate to access-expired route instead of redirecting to login
          navigate('/access-expired', { replace: true });
          return;
        }
      }

      const login = async () => {
        await auth0.loginWithRedirect({
          appState: {
            returnTo: window.location.pathname
          }
        });
      };
      if (
        window.location.pathname !== '/register' &&
        window.location.pathname !== '/self-checkout' &&
        !window.location.href.includes('/invite') &&
        !window.location.href.includes('/reset') &&
        window.location.pathname !== '/access-expired' &&
        !window.location.pathname.startsWith('/checkout/')
      ) {
        login();
      }
      return;
    }
  }, [auth0, auth0.isAuthenticated, location.pathname, navigate]);

  const value = {
    isMobile,
    accessToken,
    user,
    isBanned,
    setAccessToken,
    setUser,
    setIsBanned,
    error,
    setError,
    canceledDate,
    setCanceledDate,
    isTablet,
    bannerNotifications,
    setBannerNotifications
  };

  return (
    <Context.Provider value={value}>
      <OrganizationFetch accessExpired={accessExpired}>
        {children}
        <ErrorModal />
      </OrganizationFetch>
    </Context.Provider>
  );
};

const OrganizationFetch = ({ children, accessExpired }: { children: ReactNode; accessExpired: boolean }) => {
  const { apiRequest } = useApiRequest();
  const { accessToken, setUser, user, setCanceledDate, error, setBannerNotifications } = useContext(Context);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const implemented = true;

  const getOrganization = async () => {
    if (
      window.location.href.includes('/register') ||
      window.location.href.includes('/self-checkout') ||
      window.location.href.includes('/invite') ||
      window.location.href.includes('/reset') ||
      window.location.pathname === '/access-expired' ||
      window.location.pathname.startsWith('/checkout/')
    ) {
      setIsFinished(true);
      return;
    }
    if (accessToken && setUser && !user && implemented) {
      try {
        const res = await apiRequest<UserInformationResponseDto>('user-profile/userinfo', 'GET');
        if (res.data) {
          setUser(res.data);
          if (res.data.userType === UserType.Customer) {
            const canceledNotification = await apiRequest<Paginated<BannerCancellationNotificationListResponseDto>>(
              'notifications/cancellation',
              'GET'
            );
            if (canceledNotification.data?.docs && setCanceledDate) {
              const canceledNotificationItem = canceledNotification.data.docs[0];
              if (canceledNotificationItem) {
                setCanceledDate(canceledNotificationItem.date);
              }
            }
          }

          if (!error) {
            const bannerNotification = await apiRequest<BannerNotificationListResponseDto>('notifications/banner', 'GET');
            if (bannerNotification.data?.notifications && setBannerNotifications) {
              setBannerNotifications(bannerNotification.data.notifications);
            }
          }
        }

        // if (setError) {
        //   const res = await apiRequest<UserInvitationOptionalResponseDto>("organizations/invitations", "GET");
        //   if (res.data?.invitation) {
        //     setError({
        //       entity: ErrorEntity.CheckInvite,
        //       error: "CheckInvite",
        //       message: "CheckInvite",
        //       statusCode: 200,
        //     });
        //   }
        // }
        setIsFinished(true);
      } catch (e: any) {
        // user setup is not complete
      }
    }
  };

  useEffect(() => {
    getOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, user, accessExpired]);

  if (!implemented) {
    return <>{children}</>;
  }

  return <>{isFinished ? <>{children}</> : <Preloader />}</>;
};
